import React, { useEffect, useState, useContext } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../../../context/authcontext";
import { useTenantRole } from "../../../components/TenantRoleDisplay";
import { DEFAULT_AVATAR } from "../../../environment";
import { handleAuthError } from "../../../utils/authUtils";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import { initialSettings } from "../../../core/common/selectoption/selectoption";
import TaskDetailModal from "../../../components/TaskDetailModal";
import TablePagination from "../../../components/pagination/TablePagination";
import Swal from "sweetalert2";
import { getLifecycleTypeNames } from "../../../utils/lifecycleUtils";
import {
  Task,
  fetchTaskList,
  fetchTaskDetail,
  deleteTask,
  getCurrentSalesUserId,
} from "./TasksPage.service";
import "./TasksPage.css";

const TasksPage: React.FC = () => {
  const { auth, selectedTenantId } = useContext(AuthContext);
  const userRole = useTenantRole(selectedTenantId || 0);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [dateRange, setDateRange] = useState<{ start?: string; end?: string; utcStart?: string; utcEnd?: string }>(
    {}
  );

  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isTaskModalVisible, setIsTaskModalVisible] = useState(false);
  const [isLoadingTaskDetails, setIsLoadingTaskDetails] = useState(false);
  const [taskDetails, setTaskDetails] = useState<any>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(12);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalCount: 0,
    pageSize: 12,
  });

  const getPriorityChipClass = (priority: string | null) => {
    if (!priority) return "tp-chip-gray";
    switch (priority.toLowerCase()) {
      case "high":
        return "tp-chip-red";
      case "medium":
        return "tp-chip-amber";
      case "low":
        return "tp-chip-green";
      default:
        return "tp-chip-gray";
    }
  };

  const getStatusChipClass = (status: string | null) => {
    if (!status) return "tp-chip-gray";
    switch (status.toLowerCase()) {
      case "completed":
        return "tp-chip-green";
      case "in progress":
        return "tp-chip-purple";
      case "pending":
        return "tp-chip-blue";
      case "cancelled":
        return "tp-chip-red";
      default:
        return "tp-chip-gray";
    }
  };

  const getDaysRemainingInfo = (task: Task) => {
    if (!task.due_date) return null;
    const status = (task.status || "").toLowerCase();
    if (status === "completed" || status === "cancelled") return null;

    const diffDays = Math.ceil(
      (new Date(task.due_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
    );

    if (diffDays < 0) {
      return { label: "Overdue", variant: "red" };
    }
    if (diffDays === 0) {
      return { label: "Due today", variant: "gray" };
    }
    const label = `${diffDays} day${diffDays === 1 ? "" : "s"} remaining`;
    return diffDays > 5
      ? { label, variant: "green" }
      : { label, variant: "gray" };
  };

  const fetchTasks = async (page: number = 1) => {
    if (!selectedTenantId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await fetchTaskList({
        tenantId: selectedTenantId,
        page,
        pageSize,
        salesUserId:
          userRole?.role_key === "SALESUSER"
            ? getCurrentSalesUserId()
            : undefined,
        searchTerm,
        status: statusFilter,
        priority: priorityFilter,
        dueDateGte: dateRange.utcStart || dateRange.start,
        dueDateLte: dateRange.utcEnd || dateRange.end,
      });

      const sortedResults = [...data.results].sort((a, b) => {
        const isCompletedA = a.status?.toLowerCase() === "completed";
        const isCompletedB = b.status?.toLowerCase() === "completed";

        if (isCompletedA && !isCompletedB) return 1;
        if (!isCompletedA && isCompletedB) return -1;

        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        
        return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
      });

      setTasks(sortedResults);
      setTotalCount(data.count);
      setPagination({
        currentPage: page,
        totalCount: data.count,
        pageSize: pageSize,
      });
      return data.results;
    } catch (err: any) {
      handleAuthError(err);
      setError("Error loading tasks.");
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (start?: string, end?: string, utcStart?: string, utcEnd?: string) => {
    if (!start || !end) {
      setDateRange({});
    } else {
      setDateRange({ start, end, utcStart, utcEnd });
    }
  };

  const hasActiveFilters = () => {
    return (
      searchTerm !== "" ||
      statusFilter !== "all" ||
      priorityFilter !== "all" ||
      dateRange.start !== undefined ||
      dateRange.end !== undefined
    );
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setPriorityFilter("all");
    setDateRange({});
  };

  const handleTaskOpen = async (task: Task, event?: React.MouseEvent) => {
    event?.stopPropagation();
    setSelectedTask(task);
    setIsTaskModalVisible(true);
    setIsLoadingTaskDetails(true);

    try {
      const details = await fetchTaskDetail(task.task_id);
      setTaskDetails(details);
    } catch (err: any) {
      console.error("Error loading task details:", err);
      handleAuthError(err);
    } finally {
      setIsLoadingTaskDetails(false);
    }
  };

  const handleTaskModalClose = () => {
    setIsTaskModalVisible(false);
    setSelectedTask(null);
    setTaskDetails(null);
    setIsLoadingTaskDetails(false);
  };

  const handleTaskUpdate = async () => {
    const updatedTasks = await fetchTasks(currentPage);

    if (selectedTask && taskDetails) {
      setIsLoadingTaskDetails(true);
      try {
        const details = await fetchTaskDetail(selectedTask.task_id);
        setTaskDetails(details);

        if (updatedTasks) {
          const updatedTask = updatedTasks.find(
            (t) => t.task_id === selectedTask.task_id
          );
          if (updatedTask) {
            setSelectedTask(updatedTask);
          }
        }
      } catch (err: any) {
        console.error("Error refreshing task details:", err);
        handleAuthError(err);
      } finally {
        setIsLoadingTaskDetails(false);
      }
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchTasks(page);
  };

  const handleTaskDelete = async (task: Task, event: React.MouseEvent) => {
    event.stopPropagation();

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete the task "${task.title}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await deleteTask(task.task_id);

        Swal.fire("Deleted!", "Task has been deleted successfully.", "success");

        fetchTasks();
      } catch (err: any) {
        console.error("Error deleting task:", err);
        handleAuthError(err);
        Swal.fire(
          "Error!",
          "Failed to delete task. Please try again.",
          "error"
        );
      }
    }
  };

  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [priorityDropdownOpen, setPriorityDropdownOpen] = useState(false);

  const handleStatusFilterChange = (newStatus: string) => {
    setStatusFilter(newStatus);
    setStatusDropdownOpen(false);
  };

  const handlePriorityFilterChange = (newPriority: string) => {
    setPriorityFilter(newPriority);
    setPriorityDropdownOpen(false);
  };

  const toggleStatusDropdown = () => {
    setStatusDropdownOpen(!statusDropdownOpen);
    setPriorityDropdownOpen(false);
  };

  const togglePriorityDropdown = () => {
    setPriorityDropdownOpen(!priorityDropdownOpen);
    setStatusDropdownOpen(false);
  };

  const formatTaskDate = (dateString: string | null) => {
    if (!dateString) return "No due date";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  useEffect(() => {
    fetchTasks(currentPage);
  }, [
    selectedTenantId,
    userRole,
    searchTerm,
    statusFilter,
    priorityFilter,
    dateRange,
  ]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.custom-dropdown')) {
        setStatusDropdownOpen(false);
        setPriorityDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  useEffect(() => {
    const closeAllDropdowns = () => {
      document.querySelectorAll('.table-action .dropdown-menu').forEach((dropdown) => {
        dropdown.classList.remove('show');
        dropdown.classList.remove('dropdown-menu-fixed');
      });
    };

    const handleDropdownClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const dropdownToggle = target.closest('.dropdown-toggle');
      const dropdownMenu = target.closest('.dropdown-menu');

      if (!dropdownToggle && !dropdownMenu) {
        closeAllDropdowns();
      }
    };

    const handleDropdownToggle = (event: Event) => {
      const target = event.target as HTMLElement;
      const currentDropdown = target.closest('.table-action');
      const currentMenu = currentDropdown?.querySelector('.dropdown-menu') as HTMLElement | null;
      const currentToggle = currentDropdown?.querySelector('.dropdown-toggle') as HTMLElement | null;

      const isOpening = !!currentMenu && !currentMenu.classList.contains('show');

      closeAllDropdowns();

      if (currentMenu && isOpening) {
        currentMenu.classList.add('show');
        if (currentToggle) {
          const rect = currentToggle.getBoundingClientRect();
          currentMenu.classList.add('dropdown-menu-fixed');
          currentMenu.style.setProperty('--dd-top', `${rect.bottom + 4}px`);
          currentMenu.style.setProperty('--dd-right', `${window.innerWidth - rect.right}px`);
        }
      }
    };

    const handleTableScroll = () => {
      closeAllDropdowns();
    };


    document.addEventListener('click', handleDropdownClick);
    const dropdownToggles = document.querySelectorAll('.table-action .dropdown-toggle');
    dropdownToggles.forEach((toggle) => {
      toggle.addEventListener('click', handleDropdownToggle);
    });
    const scrollWrapper = document.querySelector('.task-table-wrapper');
    scrollWrapper?.addEventListener('scroll', handleTableScroll);
    window.addEventListener('scroll', handleTableScroll, true);


    return () => {
      document.removeEventListener('click', handleDropdownClick);
      dropdownToggles.forEach((toggle) => {
        toggle.removeEventListener('click', handleDropdownToggle);
      });
      scrollWrapper?.removeEventListener('scroll', handleTableScroll);
      window.removeEventListener('scroll', handleTableScroll, true);
    };
  }, [tasks]);

  const createEntityUrl = (task: Task): string => {
    if (!task.related_entity_id) return "#";

    const entityName = task.related_entity_name.toLowerCase();
    switch (entityName) {
      case "lead":
        return `/leads/${task.related_entity_id}`;
      case "deal":
        return `/deals/${task.related_entity_id}`;
      case "contact":
        return `/contacts/${task.related_entity_id}`;
      case "company":
        return `/companies/${task.related_entity_id}`;
      case "pipeline":
        return `/pipelines/${task.related_entity_id}`;
      case "proposal":
        return `/proposals/${task.related_entity_id}`;
      case "contract":
        return `/contracts/${task.related_entity_id}`;
      default:
        return `/${task.related_entity_name}/${task.related_entity_id}`;
    }
  };

  const getRoleDisplayText = () => {
    switch (userRole?.role_key) {
      case "CLIENTADMIN":
        return "All Organization Tasks";
      case "SALESUSER":
        return "My Tasks";
      default:
        return "Tasks";
    }
  };

  return (
    <div className="page-wrapper tasks-page">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">
                    {getRoleDisplayText()}
                    <span className="tp-header-badge">{totalCount}</span>
                  </h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <Link
                      to="#"
                      className="tp-icon-btn"
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      data-bs-original-title="Refresh"
                    >
                      <i className="ti ti-refresh-dot" onClick={() => fetchTasks(currentPage)} />
                    </Link>
                    <Link
                      to="#"
                      className="tp-icon-btn"
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      data-bs-original-title="Collapse"
                      id="collapse-header"
                    >
                      <i className="ti ti-chevrons-up" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="card tp-toolbar-card mb-0">
              <div className="tp-toolbar-section">
                <div className="row align-items-center g-2">
                  <div className="col-md-3">
                    <div className="tp-input-group">
                      <span className="tp-input-icon">
                        <i className="bi bi-search" />
                      </span>
                      <input
                        type="text"
                        className={`tp-input ${searchTerm ? "tp-input-active" : ""}`}
                        placeholder="Search tasks..."
                        value={searchTerm ?? ""}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="custom-dropdown">
                      <button
                        type="button"
                        className={`tp-dropdown-btn ${statusFilter !== "all" ? "tp-dropdown-active" : ""}`}
                        onClick={toggleStatusDropdown}
                      >
                        <span style={{ display: "flex", alignItems: "center" }}>
                          <i className="ti ti-filter me-2" />
                          {statusFilter === "all"
                            ? "All Status"
                            : statusFilter
                              .replace("_", " ")
                              .replace(/\b\w/g, (l) => l.toUpperCase())}
                        </span>
                        <i className={`ti ti-chevron-down ms-2 ${statusDropdownOpen ? 'rotate-180' : ''}`}
                          style={{ transition: 'transform 0.2s ease' }} />
                      </button>
                      {statusDropdownOpen && (
                        <div className="custom-dropdown-menu">
                          <button
                            type="button"
                            className={`custom-dropdown-item ${statusFilter === "all"
                              ? "active"
                              : ""
                              }`}
                            onClick={() => handleStatusFilterChange("all")}
                          >
                            All Status
                          </button>
                          <div className="custom-dropdown-divider" />
                          <button
                            type="button"
                            className={`custom-dropdown-item ${statusFilter === "pending"
                              ? "active"
                              : ""
                              }`}
                            onClick={() => handleStatusFilterChange("pending")}
                          >
                            Pending
                          </button>
                          <button
                            type="button"
                            className={`custom-dropdown-item ${statusFilter === "in progress"
                              ? "active"
                              : ""
                              }`}
                            onClick={() =>
                              handleStatusFilterChange("in progress")
                            }
                          >
                            In Progress
                          </button>
                          <button
                            type="button"
                            className={`custom-dropdown-item ${statusFilter === "completed"
                              ? "active"
                              : ""
                              }`}
                            onClick={() => handleStatusFilterChange("completed")}
                          >
                            Completed
                          </button>
                          <button
                            type="button"
                            className={`custom-dropdown-item ${statusFilter === "cancelled"
                              ? "active"
                              : ""
                              }`}
                            onClick={() => handleStatusFilterChange("cancelled")}
                          >
                            Cancelled
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="custom-dropdown">
                      <button
                        type="button"
                        className={`tp-dropdown-btn ${priorityFilter !== "all" ? "tp-dropdown-active" : ""}`}
                        onClick={togglePriorityDropdown}
                      >
                        <span style={{ display: "flex", alignItems: "center" }}>
                          <i className="ti ti-flag me-2" />
                          {priorityFilter === "all"
                            ? "All Priority"
                            : priorityFilter.charAt(0).toUpperCase() +
                            priorityFilter.slice(1)}
                        </span>
                        <i className={`ti ti-chevron-down ms-2 ${priorityDropdownOpen ? 'rotate-180' : ''}`}
                          style={{ transition: 'transform 0.2s ease' }} />
                      </button>
                      {priorityDropdownOpen && (
                        <div className="custom-dropdown-menu">
                          <button
                            type="button"
                            className={`custom-dropdown-item ${priorityFilter === "all"
                              ? "active"
                              : ""
                              }`}
                            onClick={() => handlePriorityFilterChange("all")}
                          >
                            All Priority
                          </button>
                          <div className="custom-dropdown-divider" />
                          <button
                            type="button"
                            className={`custom-dropdown-item ${priorityFilter === "high"
                              ? "active"
                              : ""
                              }`}
                            onClick={() => handlePriorityFilterChange("high")}
                          >
                            High
                          </button>
                          <button
                            type="button"
                            className={`custom-dropdown-item ${priorityFilter === "medium"
                              ? "active"
                              : ""
                              }`}
                            onClick={() => handlePriorityFilterChange("medium")}
                          >
                            Medium
                          </button>
                          <button
                            type="button"
                            className={`custom-dropdown-item ${priorityFilter === "low"
                              ? "active"
                              : ""
                              }`}
                            onClick={() => handlePriorityFilterChange("low")}
                          >
                            Low
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="tp-input-group">
                      <span className="tp-input-icon">
                        <i className="ti ti-calendar" />
                      </span>
                      <DateRangePicker
                        initialSettings={initialSettings}
                        onApply={(event, picker) => {
                          const localStart = picker.startDate?.format("YYYY-MM-DD");
                          const localEnd = picker.endDate?.format("YYYY-MM-DD");
                          const utcStart = picker.startDate?.utc().format("YYYY-MM-DD");
                          const utcEnd = picker.endDate?.utc().format("YYYY-MM-DD");
                          handleDateRangeChange(localStart, localEnd, utcStart, utcEnd);
                        }}
                        onCancel={() => handleDateRangeChange()}
                      >
                        <input
                          type="text"
                          className={`tp-input bookingrange ${dateRange.start || dateRange.end
                            ? "tp-input-active"
                            : ""
                            }`}
                          placeholder="Select date range"
                          value={
                            dateRange.start && dateRange.end
                              ? `${dateRange.start} - ${dateRange.end}`
                              : ""
                          }
                          readOnly
                          style={{ cursor: "pointer" }}
                        />
                      </DateRangePicker>
                    </div>
                  </div>
                  <div className="col-md-2">
                    {hasActiveFilters() && (
                      <button
                        type="button"
                        className="tp-clear-btn"
                        onClick={clearAllFilters}
                      >
                        <i className="ti ti-x" />
                        Clear Filters
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="card tp-table-card mb-4">
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : error ? (
                  <div className="alert alert-danger m-3" role="alert">
                    {error}
                  </div>
                ) : tasks.length === 0 ? (
                  <div className="tp-empty">
                    <div className="tp-empty-icon">
                      <i className="ti ti-clipboard-list" />
                    </div>
                    <h5 className="tp-empty-title">No tasks found</h5>
                    <p className="tp-empty-text">
                      {hasActiveFilters()
                        ? "Try adjusting your filters to find more tasks."
                        : "No tasks are available for your current role and tenant."}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="tp-table-inner">
                      <div className="table-responsive task-table-wrapper" style={{ position: 'relative' }}>
                        <table className="table table-nowrap tp-table mb-0 task-table mt-2" style={{ position: 'relative' }}>
                          <thead>
                            <tr>
                              <th className="task-title">Task Title</th>
                              <th className="task-responsible">Responsible Person</th>
                              <th className="task-entity">Related Entity</th>
                              <th className="task-priority">Priority</th>
                              <th className="task-status">Status</th>
                              <th className="task-due-date">Due Date</th>
                              <th className="task-created-by">Created By</th>
                              <th className="text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tasks.map((task) => {
                              const daysInfo = getDaysRemainingInfo(task);
                              return (
                                <tr
                                  key={task.task_id}
                                  className="tp-row"
                                >
                                  <td className="task-title">
                                    <div
                                      className="tp-title-cell"
                                      onClick={() => handleTaskOpen(task)}
                                    >
                                      <span className="tp-title-text" title={task.title}>
                                        {task.title}
                                      </span>
                                      <span className="tp-subtitle">
                                        {task.category}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="task-responsible">
                                    <div className="d-flex align-items-center" style={{ cursor: "default" }}>
                                      <div className="avatar avatar-xs me-2">
                                        <img
                                          src={
                                            task.responsible_persons?.avatar_url ||
                                            DEFAULT_AVATAR
                                          }
                                          alt={
                                            task.responsible_persons?.full_name ||
                                            "Unassigned"
                                          }
                                          className="tp-avatar"
                                        />
                                      </div>
                                      <span
                                        className="tp-name-text"
                                        title={
                                          task.responsible_persons?.full_name ||
                                          "Unassigned"
                                        }
                                      >
                                        {task.responsible_persons?.full_name ||
                                          "Unassigned"}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="task-entity">
                                    <Link
                                      to={createEntityUrl(task)}
                                      className="tp-entity-link"
                                      onClick={(e) => e.stopPropagation()}
                                      title={task.related_entity_title || 'No Related Entity'}
                                    >
                                      {task.related_entity_title ?
                                        (task.related_entity_title.length > 25 ? `${task.related_entity_title.substring(0, 25)}...` : task.related_entity_title) : 'No Related Entity'}
                                    </Link>
                                  </td>
                                  <td className="task-priority">
                                    <span
                                      className={`tp-chip ${getPriorityChipClass(
                                        task.priority
                                      )}`}
                                    >
                                      {task.priority || "No Priority"}
                                    </span>
                                  </td>
                                  <td className="task-status">
                                    <span
                                      className={`tp-chip ${getStatusChipClass(
                                        task.status
                                      )}`}
                                    >
                                      {task.status
                                        ? task.status.replace("_", " ")
                                        : "No Status"}
                                    </span>
                                  </td>
                                  <td className="task-due-date">
                                    <span className="tp-date-text">
                                      {formatTaskDate(task.due_date)}
                                    </span>
                                    {daysInfo && (
                                      <span className={`tp-days-tag tp-days-${daysInfo.variant} d-block`}>
                                        {daysInfo.label}
                                      </span>
                                    )}
                                  </td>
                                  <td className="task-created-by">
                                    <div className="d-flex align-items-center">
                                      <div className="avatar avatar-xs me-2">
                                        <img
                                          src={
                                            task.created_by?.avatar_url ||
                                            DEFAULT_AVATAR
                                          }
                                          alt={
                                            task.created_by?.full_name || "Unknown"
                                          }
                                          className="tp-avatar"
                                        />
                                      </div>
                                      <span
                                        className="tp-name-text"
                                        title={task.created_by?.full_name || "Unknown"}
                                      >
                                        {task.created_by?.full_name || "Unknown"}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="align-items-center">
                                    <div className="dropdown table-action">
                                      <Link
                                        to="#"
                                        className="action-icon dropdown-toggle"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <i className="fa fa-ellipsis-v" />
                                      </Link>
                                      <div className="dropdown-menu dropdown-menu-end">
                                        <button
                                          className="dropdown-item"
                                          onClick={(e) => handleTaskOpen(task, e)}
                                        >
                                          <i className="ti ti-edit text-blue" />
                                          View / Edit
                                        </button>
                                        <button
                                          className="dropdown-item"
                                          onClick={(e) => handleTaskDelete(task, e)}
                                        >
                                          <i className="ti ti-trash text-danger" />
                                          Delete
                                        </button>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    {tasks.length > 0 && (
                      <div className="tp-pagination-wrap">
                        <TablePagination
                          currentPage={pagination.currentPage || currentPage}
                          pageSize={pageSize}
                          totalItems={pagination.totalCount || totalCount}
                          filteredItems={pagination.totalCount || totalCount}
                          isFiltered={hasActiveFilters()}
                          onPageChange={handlePageChange}
                          onPageSizeChange={() => { }}
                          entityName="Tasks"
                          loading={loading}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <TaskDetailModal
        isVisible={isTaskModalVisible}
        taskDetails={taskDetails}
        isLoading={isLoadingTaskDetails}
        onClose={handleTaskModalClose}
        onTaskUpdate={handleTaskUpdate}
        tenantId={selectedTenantId}
      />
    </div>
  );
};

export default TasksPage;
